import React from 'react';
import { Tooltip } from 'react-tooltip';
import './FollowUpResultChart.scss';
import trendDown from '../../../assets/trendDown.svg';
import trendUp from '../../../assets/trendUp.svg';
import { metricValueFormat } from '../../../utils/metricValueFormat';
import { formatDateWithOrdinal } from '../../../utils/dateFormat';
import MetricGaugeVisual, { GaugeVariant } from '../../../components/metricGaugeVisual/MetricGaugeVisual';
import { TieredCircleCheckIcon, TieredCircleXIcon, TieredTargetIcon } from '../../../components/metricMonthlySummaryCard/tieredMetricIcons';
import GradientDefs, { MetricColors, TieredMetricColors } from '../../../components/metricMonthlySummaryCard/GradientDefs';
import { getExceedsTarget, getMeetsTarget, getTieredStatusVisual } from '../../../utils/metricAttainment';
import MetricProgressDirection from '../../../components/metricProgressDirection/metricProgressDirection';

interface MetricDetailsProps {
    metricTypeName: string;
    metric: {
        sessionStartDate: string;
        metricTargetValue: number;
        metricCurrentValue: number;
        metricDirectionId: number;
        sessionCommitmentAttained: boolean;
        metric_omitted: boolean;
        metricCurrentValueOverride?: number | null;
        metricCurrentValueOverrideReason?: string | null;
        coachingActionPlanSessionId: number;
        metricResultValue: number;
        targets?: string;
        metricCurrentValueAttainmentLevelLabel?: string | null;
        metricCurrentValueAttainmentRank?: number | null;
    };
    index: number;
    IsCurrentPerformance?: boolean;
    variant?: GaugeVariant;
    min?: number;
    max?: number;
}

export const FollowUpResultChart = (props: MetricDetailsProps) => {
    const {
        sessionStartDate,
        metricTargetValue: target,
        metricCurrentValue: metricValue,
        metricDirectionId,
        sessionCommitmentAttained: metricAttained,
        metric_omitted: metricOmitted,
        metricCurrentValueOverride,
        metricCurrentValueOverrideReason,
        coachingActionPlanSessionId,
        metricResultValue,
        targets,
        metricCurrentValueAttainmentLevelLabel: attainmentLevelLabel,
        metricCurrentValueAttainmentRank: attainmentRank,
    } = props?.metric || {};
    const variant = props?.variant ?? 'legacy';

    const formattedValue = metricValueFormat(metricValue, props?.metricTypeName);
    const formattedMetricResult = metricValueFormat(metricResultValue, props?.metricTypeName);

    // Status line. Legacy keeps its solid pi chip + met/not-met classes untouched. Tiered uses the
    // outline circle-check / circle-x icons and the shared getTieredStatusVisual color/icon,
    // keyed primarily off metric_attainment_level_label (per design spec) so this can never
    // disagree with the gauge's own center-value color, which uses the same helper.
    const isTiered = variant === 'tiered';
    const tieredVisual = getTieredStatusVisual(attainmentLevelLabel, attainmentRank);
    const tieredStatusColor = metricOmitted ? '#3F5765' : tieredVisual.color;
    const tieredStatusLabel = attainmentLevelLabel ?? '--';

    const legacyStatusIconClass = metricAttained ? 'pi pi-check metricMetIcon' : 'pi pi-times metricNotMetIcon';
    const legacyStatusTextClass = metricAttained ? 'metricMet' : 'MetricNotMet';
    const legacyStatusLabelText = metricAttained ? 'Metric Met' : 'Metric Not Met';

    // Precomputed to avoid a nested ternary in JSX (matches this file's no-nested-ternary style).
    let statusContent: React.ReactNode;
    if (metricOmitted) {
        statusContent = <span className="MetricOmitted">Omitted Metric</span>;
    } else if (isTiered) {
        statusContent = (
            <span className="metricTieredStatus" style={{ color: tieredStatusColor }}>
                {tieredVisual.icon === 'cross' ? <TieredCircleXIcon /> : <TieredCircleCheckIcon />}
                <span className="metricTieredStatus__label">{tieredStatusLabel}</span>
            </span>
        );
    } else {
        statusContent = (
            <>
                <i className={legacyStatusIconClass} />
                <span className={legacyStatusTextClass}>{legacyStatusLabelText}</span>
            </>
        );
    }

    const colorIndex = metricOmitted ? 5 : (props?.index ?? 0) % MetricColors.length;
    const meetsTarget = targets ? getMeetsTarget(targets) : null;
    const exceedsTarget = targets ? getExceedsTarget(targets) : null;
    const meetsTargetText = meetsTarget !== null ? metricValueFormat(meetsTarget, props?.metricTypeName) : '--';
    const exceedsTargetText = exceedsTarget !== null ? metricValueFormat(exceedsTarget, props?.metricTypeName) : '--';
    const tieredColorPair = TieredMetricColors[colorIndex] ?? TieredMetricColors[0];
    const gaugeVisual = (
        <MetricGaugeVisual
            variant={variant}
            valueText={props?.IsCurrentPerformance ? formattedMetricResult : formattedValue}
            colorIndex={colorIndex}
            metricOmitted={metricOmitted}
            value={props.IsCurrentPerformance ? metricResultValue : metricValue}
            target={target}
            metricAttained={metricAttained}
            attainmentRank={attainmentRank}
            attainmentLevelLabel={attainmentLevelLabel}
            metricValue={props.IsCurrentPerformance ? metricResultValue : metricValue}
            min={props?.min}
            max={props?.max}
            meetsTarget={meetsTarget}
            exceedsTarget={exceedsTarget}
        />
    );


    const getDirectionIcon = (id?: number) => {
        if (id === 1) return trendUp;
        if (id === 2) return trendDown;
        return null;
    };

    const getDirectionText = (id?: number) => {
        if (id === 1) return 'Ascending';
        if (id === 2) return 'Descending';
        return '';
    };

    const getCardStyle = () => {
        if (props?.IsCurrentPerformance && props?.index === 1) {
            return { background: '#EFFEFD', border: '1px solid #00B6B2' };
        }
        if (props?.IsCurrentPerformance) {
            return { background: '#FFECD3', border: '1px solid #F15523' };
        }
        return { background: '#FFFFFF', border: '1px solid #e0e4ed' };
    };

    return (
        <div className="modern-metric-card" role="button" tabIndex={0} style={{
            ...getCardStyle(),
            height: '100%',
        }}>
            <div className="metric-header">
                <h5 title={props.IsCurrentPerformance ? Date() : sessionStartDate}>{formatDateWithOrdinal(props.IsCurrentPerformance ? Date() : sessionStartDate)}</h5>
                {metricDirectionId !== undefined && (
                    <div className="direction-inline" style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        gap: '6px',
                        marginTop: '5px'
                    }}>
                        <img
                            src={getDirectionIcon(metricDirectionId) || ''}
                            alt="trend"
                            className="trend-icon"
                        />
                        <span className="direction-text">{getDirectionText(metricDirectionId)}</span>
                    </div>
                )}
            </div>

            <div className={`${isTiered ? 'followup-tiered-gauge-wrapper' : 'progress-wrapper'}`} style={{ position: 'relative' }}>
                 <GradientDefs />
                {gaugeVisual}
                {metricCurrentValueOverride != null && !props?.IsCurrentPerformance && (
                    <>
                        <i
                            className="pi pi-info-circle"
                            data-tooltip-id={`followup-override-${coachingActionPlanSessionId}`}
                            style={{
                                position: 'absolute',
                                top: '20px',
                                right: '-6px',
                                cursor: 'pointer',
                                fontSize: '30px',
                                color: '#C0392B',
                                backgroundColor: '#FADBD8',
                                borderRadius: '50%',
                                padding: '4px',
                            }}
                        />
                        <Tooltip
                            id={`followup-override-${coachingActionPlanSessionId}`}
                            className="rounded tooltip override-tooltip"
                            events={['click']}
                            place="right-start"
                            closeOnEsc
                            positionStrategy="fixed"
                            style={{ overflow: 'visible', maxHeight: 'none', zIndex: 99999 }}
                        >
                            <div className="override-tooltip__content" style={{ maxWidth: '200px', wordBreak: 'break-word' }}>
                                <span className="override-tooltip__label">Overridden Data</span>
                                <span className="override-tooltip__value">
                                    {metricValueFormat(metricCurrentValueOverride, props.metricTypeName)}
                                </span>
                                <span className="override-tooltip__description">
                                    {metricCurrentValueOverrideReason ?? ''}
                                </span>
                            </div>
                        </Tooltip>
                    </>
                )}
            </div>
            <div className='row mt-2'>
                <div className="col-12 text-center">
                    {statusContent}
                </div>
            </div>
            <div className="metric-footer">
                {!metricOmitted && variant === 'tiered' && (
                    <>
                        <hr className="metricTieredDivider" />
                        <div className="metricTargetThresholds text-center">
                            <div className="metricTargetThresholds__row" style={{ color: tieredColorPair.dark }}>
                                <TieredTargetIcon />
                                <span>Meets at: {meetsTargetText}</span>
                            </div>
                            <div className="metricTargetThresholds__row" style={{ color: tieredColorPair.light }}>
                                <TieredTargetIcon />
                                <span>Exceeds at: {exceedsTargetText}</span>
                            </div>
                        </div>
                    </>
                )}

                <div className="col-12 text-center">
                    {!metricOmitted && variant === 'legacy' && metricDirectionId && target && (
                        <MetricProgressDirection
                            metricDirectionId={metricDirectionId}
                            trendPercentage={target}
                            sType=''
                        />
                    )}
                </div>
            </div>
        </div>
    );
};